// MitID id_token interception is handled in index.html (before any JS module loads)
// to prevent Supabase gotrue-js from interpreting it as an OAuth callback

import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { Capacitor } from "@capacitor/core";
import "./index.css";
import { installStartupDiagnostics, recordStartupDiagnostic } from "./lib/startupDiagnostics";
import { installMedianNavigationListener } from "./lib/medianNavigation";

installStartupDiagnostics();
installMedianNavigationListener();

const rootElement = document.getElementById("root");
const startupPrefix = "[TaskVeoStartup]";

let reactRenderCompleted = false;

const stringifyError = (error: unknown): string => {
  if (error instanceof Error) {
    return [error.name, error.message, error.stack].filter(Boolean).join("\n");
  }

  if (typeof error === "string") return error;

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

const showRootMessage = (title: string, detail?: string) => {
  if (!rootElement) return;

  const wrapper = document.createElement("div");
  wrapper.setAttribute("role", "status");
  Object.assign(wrapper.style, {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    background: "#F9FAF7",
    color: "#141F1A",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    textAlign: "center",
  });

  const heading = document.createElement("div");
  heading.textContent = title;
  Object.assign(heading.style, {
    fontSize: "18px",
    fontWeight: "700",
    lineHeight: "1.35",
  });
  wrapper.appendChild(heading);

  if (detail) {
    const pre = document.createElement("pre");
    pre.textContent = detail.slice(0, 3000);
    Object.assign(pre.style, {
      marginTop: "16px",
      maxWidth: "100%",
      maxHeight: "45vh",
      overflow: "auto",
      padding: "12px",
      borderRadius: "8px",
      background: "#141F1A",
      color: "#F9FAF7",
      fontSize: "12px",
      lineHeight: "1.45",
      textAlign: "left",
      whiteSpace: "pre-wrap",
    });
    wrapper.appendChild(pre);
  }

  const retryButton = document.createElement("button");
  retryButton.type = "button";
  retryButton.textContent = "Prøv igen";
  Object.assign(retryButton.style, {
    marginTop: "20px",
    padding: "10px 20px",
    borderRadius: "8px",
    border: "none",
    background: "#141F1A",
    color: "#F9FAF7",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  });
  retryButton.addEventListener("click", () => {
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    } catch {
      // ignore
    }
    window.location.reload();
  });
  wrapper.appendChild(retryButton);

  rootElement.replaceChildren(wrapper);
  hideSplash();
};

const reportStartupError = (label: string, error: unknown) => {
  const detail = stringifyError(error);
  console.error(`${startupPrefix} ${label}`, error);

  if (!reactRenderCompleted) {
    showRootMessage(`TaskVeo startup fejl: ${label}`, detail);
  }
};

const CHUNK_RELOAD_FLAG = 'taskveo_chunk_reload_done';

const hideSplash = () => {
  try {
    const hide = (window as Window & { __taskveoHideSplash?: () => void }).__taskveoHideSplash;
    if (typeof hide === "function") hide();
  } catch {
    // ignore
  }
};

const isChunkLoadError = (error: unknown): boolean => {
  const msg = error instanceof Error ? `${error.name} ${error.message}` : String(error ?? '');
  return (
    /Importing a module script failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /ChunkLoadError/i.test(msg)
  );
};

const getChunkReloadKey = (error: unknown): string => {
  const message = error instanceof Error ? error.message : String(error ?? 'unknown');
  const match = message.match(/https?:\/\/[^\s)"']+\/assets\/[^\s)"']+/i);
  const chunkId = match?.[0] || message.slice(0, 160);
  return `${CHUNK_RELOAD_FLAG}:${chunkId}`;
};

const nativeInfo = (() => {
  try {
    return {
      isNative: Capacitor.isNativePlatform(),
      platform: Capacitor.getPlatform(),
    };
  } catch (error) {
    reportStartupError("Capacitor platform check failed", error);
    return { isNative: false, platform: "unknown" };
  }
})();

console.info("TaskVeo native startup begin", nativeInfo);

window.addEventListener("error", (event) => {
  reportStartupError("window error", event.error || event.message);
});

window.addEventListener("unhandledrejection", (event) => {
  reportStartupError("unhandled rejection", event.reason);
});

// Undgå sort WKWebView-flash før CSS/React loader (især når iPhone er i mørk tilstand)
try {
  if (nativeInfo.isNative) {
    document.documentElement.classList.add("taskveo-native-shell");
    if (nativeInfo.platform === "android") {
      document.documentElement.classList.add("taskveo-native-android");
    }
    document.documentElement.style.backgroundColor = "#F9FAF7";
    document.documentElement.style.colorScheme = "light";
    document.body.style.backgroundColor = "#F9FAF7";
  }
} catch {
  // ignore
}

// Detect if running in iframe (Lovable preview) and add class to body
const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch (e) {
    // If we can't access window.top due to same-origin policy, we're likely in an iframe
    return true;
  }
})();

// Add class when DOM is ready
const addIframeClass = () => {
  if (isInIframe && document.body) {
    document.body.classList.add('in-iframe');
  }
};

// Try immediately, on DOMContentLoaded, and after a short delay as fallback
addIframeClass();
document.addEventListener('DOMContentLoaded', addIframeClass);
setTimeout(addIframeClass, 100);

const isPreviewHost =
  window.location.hostname.includes('id-preview--') ||
  window.location.hostname.includes('lovableproject.com');

// Median er ikke Capacitor: `Capacitor.isNativePlatform()` er false i Median-
// webviewen. Uden denne UA/bridge-detektion registrerede appen en service
// worker inde i Median (især Android), hvor en gammel/klemt worker kunne
// fastholde et forældet HTML-shell → manglende JS-chunks → frossen app,
// som kun kunne løses ved at rydde app-data og cache manuelt.
const isMedianShell = (() => {
  try {
    const ua = navigator.userAgent || '';
    if (/MedianIOS\//i.test(ua) || /MedianAndroid\//i.test(ua) || /\bmedian\b/i.test(ua)) return true;
    const w = window as unknown as Record<string, unknown>;
    return Boolean(w.median || w.gonative || w.goNative);
  } catch {
    return false;
  }
})();

const clearServiceWorkerCaches = async (includePushWorker = false) => {
  if (!('serviceWorker' in navigator)) return false;

  const registrations = await navigator.serviceWorker.getRegistrations();
  const legacyRegistrations = registrations.filter((registration) => {
    const scriptURL =
      registration.active?.scriptURL ||
      registration.waiting?.scriptURL ||
      registration.installing?.scriptURL ||
      '';

    // Never touch the OneSignal worker: it lives in its own scope and is
    // owned by the OneSignal SDK (no-op when the flag is off, since it is
    // then never registered).
    if (scriptURL.includes('/push/onesignal/')) return false;

    return includePushWorker || !scriptURL.endsWith('/push-sw.js');
  });
  const hadController = Boolean(
    legacyRegistrations.length > 0 &&
    (!navigator.serviceWorker.controller || !navigator.serviceWorker.controller.scriptURL.endsWith('/push-sw.js')),
  );

  await Promise.all(legacyRegistrations.map((registration) => registration.unregister()));

  if ((includePushWorker || legacyRegistrations.length > 0) && 'caches' in window) {
    const cacheKeys = await caches.keys();
    await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
  }

  const resetFlag = 'preview_sw_reset_done';
  if (hadController && !sessionStorage.getItem(resetFlag)) {
    sessionStorage.setItem(resetFlag, '1');
    window.location.reload();
    return true;
  }

  sessionStorage.removeItem(resetFlag);
  return false;
};

// Remove old Workbox/PWA service workers and caches. They previously cached the
// HTML shell, which can keep normal browsers on stale builds while fresh/private
// sessions see a different version. Push notifications use a separate no-cache
// worker below.
if ('serviceWorker' in navigator && !nativeInfo.isNative && !isMedianShell) {
  window.addEventListener('load', async () => {
    try {
      if (isInIframe || isPreviewHost) {
        await clearServiceWorkerCaches(true);
        return;
      }

      const didReset = await clearServiceWorkerCaches();
      if (didReset) return;

      await navigator.serviceWorker.register('/push-sw.js');
    } catch (error) {
      // Graceful fallback - app continues to work without SW
      // Push notifications will still work if manually re-enabled later
    }
  });
}

// I Median-appen må der aldrig ligge en service worker: push kommer fra
// OneSignal natively. Ryd derfor altid op i arvede workers/caches, så en
// stale worker ikke kan fryse appen ved næste opstart.
if ('serviceWorker' in navigator && isMedianShell) {
  window.addEventListener('load', () => {
    void clearServiceWorkerCaches(true).catch(() => {
      // Oprydning må aldrig blokere opstart.
    });
  });
}

const bootstrap = async () => {
  recordStartupDiagnostic("REACT BOOTSTRAP START", "main.tsx bootstrap", {
    url: window.location.href,
  });
  const suppressAppBootstrap = (window as Window & { __TASKVEO_SUPPRESS_APP_BOOTSTRAP?: boolean }).__TASKVEO_SUPPRESS_APP_BOOTSTRAP === true;
  if (suppressAppBootstrap) {
    console.info(`${startupPrefix} app bootstrap suppressed by native callback bridge`);
    return;
  }


  if (!rootElement) {
    throw new Error("Missing #root element");
  }

  if (!nativeInfo.isNative) {
    const { migrateBrowserAuthStorage } = await import('./lib/browserAuthStorage');
    if (migrateBrowserAuthStorage()) {
      return;
    }
  }

  try {
    console.info(`${startupPrefix} before native init`, nativeInfo);
    const { initNativeApp } = await import("./lib/nativeApp");
    await initNativeApp();
    console.info(`${startupPrefix} native init completed (pre-render)`);

    const { installMedianOneSignalClickHandler } = await import("./lib/medianOneSignal");
    installMedianOneSignalClickHandler();

    console.info(`${startupPrefix} before App import`, nativeInfo);
    const { default: App } = await import("./App.tsx");
    console.info(`${startupPrefix} after App import`);

    console.info(`${startupPrefix} before React render`);
    recordStartupDiagnostic("FØR REACT RENDER", "main.tsx bootstrap", { url: window.location.href });
    createRoot(rootElement).render(
      <HelmetProvider>
        <App />
      </HelmetProvider>
    );
    reactRenderCompleted = true;
    console.info(`${startupPrefix} after React render`);
    recordStartupDiagnostic("EFTER REACT RENDER", "main.tsx bootstrap", { url: window.location.href });

    // Glidende overgang: skjul splash på næste frame, når første skærm er malet.
    requestAnimationFrame(() => requestAnimationFrame(hideSplash));

    void import("./lib/nativeApp")
      .then(({ consumeNativeLaunchUrl }) => consumeNativeLaunchUrl())
      .then(() => console.info(`${startupPrefix} launch URL check done`))
      .catch((error) => reportStartupError("native launch URL auth check failed", error));

    void import("./lib/globalErrorHandler")
      .then(({ installGlobalErrorHandlers }) => {
        installGlobalErrorHandlers();
        console.info(`${startupPrefix} global error handlers installed`);
      })
      .catch((error) => reportStartupError("global error handler import failed", error));

    void import("./lib/connectionHealth")
      .then(({ installConnectionMonitor }) => {
        installConnectionMonitor();
        console.info(`${startupPrefix} connection monitor installed`);
      })
      .catch((error) => reportStartupError("connection monitor import failed", error));
  } catch (error) {
    if (isChunkLoadError(error)) {
      const reloadKey = getChunkReloadKey(error);
      let alreadyRetried = false;
      try {
        alreadyRetried = sessionStorage.getItem(reloadKey) === '1';
      } catch {
        // ignore
      }
      if (!alreadyRetried) {
        try {
          sessionStorage.setItem(reloadKey, '1');
        } catch {
          // ignore
        }
        console.warn(`${startupPrefix} chunk load failed — clearing SW caches and retrying once`);
        try {
          await clearServiceWorkerCaches(true);
        } catch {
          // ignore
        }
        window.location.reload();
        return;
      }
    }
    reportStartupError("bootstrap failed", error);
  }
};

void bootstrap();
